import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaBook, FaUniversity, FaAward, FaArrowRight } from 'react-icons/fa';
import diplomaImg from "../assets/courses/diploma.gif";
import UGImg from "../assets/courses/ug.gif";
import PGImg from "../assets/courses/pg.gif";
import PhdImg from "../assets/courses/phd.gif";
import { Link } from "react-router-dom";

const ProgramCard = ({ image, title, description, link, icon: Icon }) => {
    const [isHovered, setIsHovered] = useState(false);
    
    return (
        <motion.div 
            className='relative overflow-hidden rounded-2xl shadow-xl bg-white group h-full flex flex-col'
            whileHover={{ 
                scale: 1.03,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            }}
            transition={{ duration: 0.3 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className='relative'>
                <motion.img 
                    loading='lazy' 
                    src={image} 
                    alt={title} 
                    className='w-full h-60 md:h-64 object-cover rounded-t-2xl'
                    animate={{ scale: isHovered ? 1.05 : 1 }}
                    transition={{ duration: 0.5 }}
                />
                <div className='absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end'>
                    <div className='p-6 w-full'>
                        <motion.div 
                            className='bg-white/20 backdrop-blur-sm p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4 mx-auto'
                            initial={{ rotate: 0 }}
                            animate={{ rotate: isHovered ? 15 : 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Icon className='text-white text-3xl' />
                        </motion.div>
                    </div>
                </div>
            </div>
            
            <div className='p-6 flex-grow'>
                <h3 className='text-2xl font-bold text-[#0e4d38] mb-3'>{title}</h3>
                <p className='text-gray-600 hidden md:block'>{description}</p>
            </div>
            
            {/* Fixed positioned button at bottom of card */}
            <div className='p-6 pt-0 mt-auto'>
                <Link to={link} className='block'>
                    <motion.button 
                        className='w-full bg-[#24933c] hover:bg-[#0e4d38] text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors duration-300'
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <span>Explore Program</span>
                        <motion.div
                            animate={{ x: isHovered ? 5 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <FaArrowRight />
                        </motion.div>
                    </motion.button>
                </Link>
            </div>
        </motion.div>
    );
};

const Programs = () => {
    const programData = [
        {
            image: diplomaImg,
            title: 'Diploma Programs',
            description: 'Specialized short-term courses designed for skill enhancement and rapid career advancement with industry-focused training.',
            link: "/programmes/diploma-courses",
            icon: FaAward
        },
        {
            image: UGImg,
            title: 'Undergraduate Programs',
            description: 'Comprehensive bachelor degrees across various disciplines to build strong academic foundations with practical experience.',
            link: "/programmes/ug-courses",
            icon: FaUniversity
        },
        {
            image: PGImg,
            title: 'Postgraduate Programs',
            description: 'Advanced master\'s level courses providing in-depth knowledge, specialized skills, and extensive research opportunities.',
            link: "/programmes/pg-courses",
            icon: FaBook
        },
        {
            image: PhdImg,
            title: 'Doctoral Programs',
            description: 'Research-driven Ph.D. programs fostering innovation and academic excellence with mentorship from leading scholars.',
            link: "/programmes/best-university-for-phd-in-dehradun-uttarakhand",
            icon: FaGraduationCap
        }
    ];

    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className='bg-gradient-to-b from-gray-50 to-white py-24'
        >
            <div className='container mx-auto px-4 max-w-7xl'>
                <motion.div 
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className='text-center mb-16'
                >
                    <span className='px-4 py-2 bg-green-100 text-[#0e4d38] rounded-full text-sm font-medium mb-4 inline-block'>Academic Excellence</span>
                    <h2 className='text-4xl md:text-5xl font-bold text-[#0e4d38] mb-6'>
                        Shape Your Future With Our <br className='hidden md:block' />
                        <span className='relative'>
                            <span className='relative z-10'>Academic Programs</span>
                            <span className='absolute bottom-2 left-0 w-full h-3 bg-green-200 opacity-50 z-0'></span>
                        </span>
                    </h2>
                    <p className='text-gray-600 max-w-3xl mx-auto text-lg'>
                        Discover a wide range of academic opportunities tailored to nurture your potential
                        and prepare you for success in today's competitive global landscape.
                    </p>
                </motion.div>

                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
                    {programData.map((program, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
                        >
                            <ProgramCard {...program} />
                        </motion.div>
                    ))}
                </div>
                
                {/* <motion.div 
                    className='text-center mt-16'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 0.8 }}
                >
                    <Link to="/programmes">
                        <button className='px-8 py-4 border-2 border-[#0e4d38] text-[#0e4d38] rounded-xl font-bold hover:bg-[#0e4d38] hover:text-white transition-all duration-300'>
                            View All Programs
                        </button>
                    </Link>
                </motion.div> */}
            </div>
            
            {/* Decorative elements */}
            <div className='absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full opacity-20 blur-3xl hidden lg:block'></div>
            <div className='absolute bottom-20 right-10 w-40 h-40 bg-green-300 rounded-full opacity-20 blur-3xl hidden lg:block'></div>
        </motion.div>
    );
}

export default Programs;